import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FunnelVariableDto, TypeContextVariable } from 'api/funnel';
import { funnelExpressionsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';

interface VariableFormValues {
  variableId: string;
  value: number | '';
  isUserConfigurable: boolean;
  possibleValues: string;
  unite: string;
}

const validationSchema = Yup.object().shape({
  variableId: Yup.string().required('La variable est requise'),
  value: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .required('La valeur est requise'),
});

interface VariableValueModalProps {
  /** Absent en création. */
  variable?: FunnelVariableDto;
  /** Création : tunnel porteur de la variable. */
  tunnelId?: string;
  /**
   * Variables déjà rattachées au tunnel : le back refuse qu'une variable soit redéfinie
   * deux fois sur le même objet, autant ne pas les proposer.
   */
  usedVariableIds?: string[];
  onSuccess: () => void;
}

/**
 * Rattachement d'une variable au tunnel et modification de sa valeur.
 *
 * Une variable se résume à une valeur. Les valeurs possibles et l'unité n'ont de sens
 * que si le courtier peut la régler lui-même : elles n'apparaissent donc qu'une fois le
 * commutateur activé, plutôt que d'être proposées en permanence comme dans l'ancien
 * écran, où l'on ne savait pas à quoi elles servaient.
 */
export const VariableValueModal: FC<VariableValueModalProps> = ({
  variable,
  tunnelId,
  usedVariableIds = [],
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<VariableFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(variable);

  // Le référentiel n'est chargé qu'en création : en modification, la variable est figée.
  const fetchVariables = useCallback(() => funnelExpressionsAPI.getVariables(), []);
  const variables = useAsyncResource(fetchVariables, !isEditMode);

  const variableItems = useMemo(
    () =>
      (variables.data ?? [])
        .filter((item) => item.id && !usedVariableIds.includes(item.id))
        .map((item) => ({
          key: item.id as string,
          label: item.libelle || item.code || 'Variable sans libellé',
          trailingLabel: item.code ?? undefined,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [variables.data, usedVariableIds]
  );

  const initialValues: VariableFormValues = {
    variableId: variable?.variable?.id ?? '',
    value: variable?.value ?? '',
    isUserConfigurable: variable?.isUserConfigurable ?? false,
    possibleValues: variable?.possibleValues ?? '',
    unite: variable?.unite ?? '',
  };

  const handleSubmit = async (values: VariableFormValues) => {
    if (isSubmitting) return;
    if (isEditMode && !variable?.id) return;
    if (!isEditMode && !tunnelId) return;

    // Sans réglage par le courtier, ces deux champs n'ont pas d'objet.
    const possibleValues = values.isUserConfigurable
      ? values.possibleValues.trim() || null
      : null;
    const unite = values.isUserConfigurable ? values.unite.trim() || null : null;

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await funnelExpressionsAPI.updateVariableValue({
          updateVariableValueCommand: {
            id: variable?.id as string,
            value: Number(values.value),
            isUserConfigurable: values.isUserConfigurable,
            possibleValues,
            unite,
          },
        });
      } else {
        await funnelExpressionsAPI.createVariableValue({
          createVariableValueCommand: {
            variableId: values.variableId,
            value: Number(values.value),
            contextObjetId: tunnelId as string,
            typeContextVariable: TypeContextVariable.Tunnel,
            isUserConfigurable: values.isUserConfigurable,
            possibleValues,
            unite,
          },
        });
      }

      toast.success(isEditMode ? 'Variable mise à jour' : 'Variable ajoutée au tunnel');
      onSuccess();
      closeDialog();
    } catch (error) {
      console.error("Enregistrement de la variable impossible", error);
      // Doublon de variable, variable inconnue… : le back rédige le message.
      const parsed = await parseApiError(error);
      toast.error(parsed.message || "L'enregistrement a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <ModalShell
        title={variable?.variable?.libelle || 'Ajouter une variable'}
        subtitle={
          variable?.variable?.code ||
          (isEditMode ? undefined : 'Valeur utilisée par les formules de ce tunnel')
        }
        isLoading={variables.isLoading}
        submit={{
          label: 'Sauvegarder',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting,
        }}
      >
        <Form className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5">
          {!isEditMode && (
            <FormikDropdown
              name="variableId"
              label="Variable"
              required
              items={variableItems}
              disabled={isSubmitting}
              placeholder={
                variableItems.length
                  ? 'Sélectionner une variable'
                  : 'Toutes les variables sont déjà rattachées'
              }
            />
          )}

          <FormikInputText name="value" label="Valeur" type="number" required />

          <div className="border-t border-gray-100 pt-4">
            <FormikSwitch
              name="isUserConfigurable"
              label="Variable configurable par le courtier"
            />
            <p className="mt-1 text-xs text-gray-400">
              Si activé, le courtier choisit la valeur au comparateur, dans les limites
              définies ci-dessous.
            </p>
          </div>

          <ConfigurableFields />
        </Form>
      </ModalShell>
    </Formik>
  );
};

const ConfigurableFields: FC = () => {
  const { values } = useFormikContext<VariableFormValues>();
  if (!values.isUserConfigurable) return null;

  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
      <FormikInputText
        name="possibleValues"
        label="Valeurs possibles"
        placeholder="Laisser vide si la valeur est libre"
      />
      <FormikInputText name="unite" label="Unité" placeholder="ex. €, %, m²" />
    </div>
  );
};
